import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";

const BACKEND = window.location.hostname === "localhost"
    ? "http://localhost:3001"
    : "https://myprofilecorner.com/api";

const MAX_RETRIES = 8;
const RETRY_DELAY = 4000;

export default function PaymentSuccess() {
    const [searchParams] = useSearchParams();
    const { user, refreshPremium } = useAuth();
    const navigate = useNavigate();
    const [status, setStatus] = useState("verifying");
    const [attempt, setAttempt] = useState(0);
    const orderId = searchParams.get("order_id");
    const returnPath = localStorage.getItem("paymentReturnPath") || "/themes";
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    useEffect(() => {
        if (user === undefined) return; // auth still loading

        let retries = 0;
        let timer;

        const verify = async () => {
            if (!mountedRef.current) return;
            if (!orderId) { setStatus("failed"); return; }

            // User not yet restored from storage — wait and retry
            if (!user) {
                if (retries < MAX_RETRIES) {
                    retries++;
                    setAttempt(retries);
                    timer = setTimeout(verify, RETRY_DELAY);
                } else {
                    setStatus("failed");
                }
                return;
            }

            try {
                const res = await fetch(`${BACKEND}/verify-payment`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ orderId, userId: user.uid }),
                });
                const data = await res.json();

                if (!mountedRef.current) return;

                if (data.activated) {
                    await refreshPremium();
                    if (mountedRef.current) setStatus("success");
                } else if (retries < MAX_RETRIES) {
                    // Not activated yet — webhook may still be in transit, retry
                    retries++;
                    setAttempt(retries);
                    timer = setTimeout(verify, RETRY_DELAY);
                } else {
                    setStatus("failed");
                }
            } catch {
                if (!mountedRef.current) return;
                if (retries < MAX_RETRIES) {
                    retries++;
                    setAttempt(retries);
                    timer = setTimeout(verify, RETRY_DELAY);
                } else {
                    setStatus("failed");
                }
            }
        };

        verify();
        return () => clearTimeout(timer);
    }, [user]);

    return (
        <div style={{ minHeight: "100vh", background: "#f0f2f5", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem", fontFamily: "'Segoe UI', sans-serif" }}>
            <div style={{ background: "#fff", borderRadius: "24px", padding: "3rem 2rem", width: "100%", maxWidth: "380px", textAlign: "center", boxShadow: "0 8px 40px rgba(0,0,0,0.10)" }}>

                {status === "verifying" && (
                    <>
                        <div style={{ width: "64px", height: "64px", borderRadius: "50%", border: "4px solid #e5e7eb", borderTopColor: "#0AADA1", margin: "0 auto 24px", animation: "spin 0.9s linear infinite" }} />
                        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                        <h2 style={{ margin: "0 0 8px", fontSize: "20px", fontWeight: "800", color: "#111" }}>Verifying payment…</h2>
                        <p style={{ margin: "0 0 6px", fontSize: "14px", color: "#9ca3af" }}>
                            {attempt === 0 ? "Please wait a moment" : `Still checking… (${attempt}/${MAX_RETRIES})`}
                        </p>
                        {attempt > 2 && (
                            <p style={{ margin: 0, fontSize: "12px", color: "#d1d5db" }}>Payment confirmed — activating your account</p>
                        )}
                    </>
                )}

                {status === "success" && (
                    <>
                        <div style={{ width: "72px", height: "72px", borderRadius: "50%", background: "linear-gradient(135deg,#0AADA1,#0f8f85)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px" }}>
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                        </div>
                        <h2 style={{ margin: "0 0 8px", fontSize: "22px", fontWeight: "800", color: "#111" }}>You're Premium! 🎉</h2>
                        <p style={{ margin: "0 0 8px", fontSize: "14px", color: "#6b7280" }}>All premium themes are now unlocked.</p>
                        {orderId && <p style={{ margin: "0 0 28px", fontSize: "11px", color: "#d1d5db", fontFamily: "monospace" }}>Order: {orderId}</p>}
                        <button
                            onClick={() => { localStorage.removeItem("paymentReturnPath"); navigate(returnPath); }}
                            style={{ width: "100%", padding: "14px", borderRadius: "14px", border: "none", background: "linear-gradient(135deg,#0AADA1,#0f8f85)", color: "#fff", fontSize: "15px", fontWeight: "700", cursor: "pointer" }}
                        >
                            Start Using Premium →
                        </button>
                    </>
                )}

                {status === "failed" && (
                    <>
                        <div style={{ width: "72px", height: "72px", borderRadius: "50%", background: "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px" }}>
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                        </div>
                        <h2 style={{ margin: "0 0 8px", fontSize: "20px", fontWeight: "800", color: "#111" }}>Payment received</h2>
                        <p style={{ margin: "0 0 6px", fontSize: "14px", color: "#6b7280" }}>Your payment was successful but activation is taking longer than usual.</p>
                        <p style={{ margin: "0 0 28px", fontSize: "13px", color: "#9ca3af" }}>Sign out and sign back in — your premium will be active within a minute.</p>
                        <button
                            onClick={() => { localStorage.removeItem("paymentReturnPath"); navigate(returnPath); }}
                            style={{ width: "100%", padding: "14px", borderRadius: "14px", border: "none", background: "#111", color: "#fff", fontSize: "15px", fontWeight: "700", cursor: "pointer", marginBottom: "10px" }}
                        >
                            Go Back
                        </button>
                        <button
                            onClick={() => { setStatus("verifying"); setAttempt(0); }}
                            style={{ width: "100%", padding: "12px", borderRadius: "14px", border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151", fontSize: "14px", fontWeight: "600", cursor: "pointer" }}
                        >
                            Try Again
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
